import {useState} from 'react';
import {CheckCircle2, X, XCircle} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Spinner,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {useBulkReviewQcItems} from '@/api/hooks';
import {showToast} from '@/lib/toast';

/** Apply one QC decision to every selected queue item. Approve-with-edits stays per item. */
export function QcBulkBar({
  selectedIds,
  onClear
}: {
  readonly selectedIds: ReadonlySet<string>;
  readonly onClear: () => void;
}) {
  const bulk = useBulkReviewQcItems();
  const [rejecting, setRejecting] = useState(false);
  const [notes, setNotes] = useState('');
  const count = selectedIds.size;

  if (count === 0) {
    return null;
  }

  async function run(action: 'approve' | 'reject') {
    const {succeeded, failed} = await bulk.mutateAsync({
      ids: [...selectedIds],
      action,
      notes: notes.trim()
    });
    const verb = action === 'approve' ? 'Approved' : 'Rejected';
    showToast(
      failed > 0
        ? `${verb} ${succeeded}; ${failed} failed (refresh and retry).`
        : `${verb} ${succeeded}.`,
      failed > 0 ? 'error' : 'success'
    );
    setRejecting(false);
    setNotes('');
    onClear();
  }

  // The bulk-bar surface, ruled along its bottom rather than its top: this bar heads its TablePanel,
  // so the panel's own border already closes the top edge and the rule belongs above the table.
  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="border-border bg-card flex flex-wrap items-center gap-2 border-b px-5 py-2">
      <span className="text-xs font-medium whitespace-nowrap">{count} selected</span>
      <Button type="button" size="sm" disabled={bulk.isPending} onClick={() => void run('approve')}>
        {bulk.isPending ? <Spinner /> : <CheckCircle2 />}
        Approve {count}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="destructive"
        disabled={bulk.isPending}
        onClick={() => {
          setRejecting(true);
        }}>
        <XCircle />
        Reject {count}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Clear selection"
        className="ml-auto"
        onClick={onClear}>
        <X />
      </Button>

      <Dialog open={rejecting} onOpenChange={setRejecting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject {count} items</DialogTitle>
            <DialogDescription>The same notes are recorded against every item.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="qc-bulk-reject-notes">Review notes</FieldLabel>
            <Textarea
              id="qc-bulk-reject-notes"
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
              }}
            />
            <FieldDescription>Required.</FieldDescription>
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setRejecting(false);
              }}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={notes.trim() === '' || bulk.isPending}
              onClick={() => void run('reject')}>
              {bulk.isPending ? <Spinner /> : null}
              Reject {count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
